import { useCallback, useEffect, useState } from 'react'
import { APP_SETTINGS } from '../config/appSettings'
import { readStripDismissals, readStripEligible, shouldShowSignInStrip, writeStripDismissals, writeStripEligible } from './signInStripRules'

/**
 * Whether Browse shows the sign-in strip for this scope. The dismissals and the "brand-new workspace" mark are read once per scope and nothing
 * shows until they have been read, so a person who dismissed it never sees it flash. `welcomeShowing` is the one signal that a workspace is
 * brand new (the welcome only shows for a scope that has never held a note): the first time it is true the mark is written, and only a marked
 * scope ever gets the strip. `dismiss` hides it at once and records the note count it was dismissed at, which decides whether it comes back
 * (signInStripRules.ts).
 */
export function useSignInStrip({ scope, guest, inventoryReady, noteCount, welcomeShowing }: { scope: string; guest: boolean; inventoryReady: boolean; noteCount: number; welcomeShowing: boolean }) {
  const [state, setState] = useState<{ scope: string; dismissals: number[]; eligible: boolean } | null>(null)
  useEffect(() => {
    let live = true
    void Promise.all([readStripDismissals(scope), readStripEligible(scope)]).then(
      ([dismissals, eligible]) => { if (live) setState({ scope, dismissals, eligible }) },
      () => { if (live) setState({ scope, dismissals: [1, 1], eligible: false }) },
    )
    return () => { live = false }
  }, [scope])
  const known = state?.scope === scope
  const eligible = known && state.eligible
  useEffect(() => {
    if (!known || eligible || !welcomeShowing) return
    setState(current => current && current.scope === scope ? { ...current, eligible: true } : current)
    void writeStripEligible(scope).catch(() => undefined)
  }, [known, eligible, welcomeShowing, scope])
  const dismiss = useCallback(() => {
    if (!state || state.scope !== scope) return
    const dismissals = [...state.dismissals, noteCount]
    setState({ ...state, dismissals })
    void writeStripDismissals(scope, dismissals).catch(() => undefined)
  }, [scope, state, noteCount])
  return { show: known && shouldShowSignInStrip({ guest, brandNew: state.eligible, inventoryReady, noteCount, dismissals: state.dismissals, resurfaceAtNotes: APP_SETTINGS.onboarding.signInStripResurfaceAtNotes }), dismiss }
}
